import React from "react";

import { useTheme } from "../../../context/theme";
import { Role } from "../../../styles/colors/types";
import { IconName } from "../../../styles/icons/icons";
import { JustifyContent } from "../../../types";
import { isButton, isButtonLink } from "../../../utils/isButton";
import { Button, ButtonLink, IconButton } from "../../controls";
import { ButtonFill, ButtonSize } from "../../controls/Button/types";
import { Box, Inline } from "../../layout";
import { Text } from "../../typography";
import Icon from "../Icon";
import { AlertTone } from "./consts";
import { ButtonPropsType } from "./types";

type Props = {
  tone?: AlertTone;
  iconName?: IconName;
  buttonProps?: ButtonPropsType;
  children?: string | React.ReactNode;
  onCloseClick?: () => void;
  justifyContent?: JustifyContent;
};

export default function Alert({
  tone = "primary",
  iconName,
  buttonProps,
  children,
  onCloseClick,
  justifyContent = "space-between"
}: Props) {
  const { backgroundColor, borderColor } = getColours(tone);

  return (
    <Box
      display="flex"
      paddingX="12"
      paddingY="large"
      alignItems="center"
      justifyContent={justifyContent}
      backgroundColor={backgroundColor}
      borderStyle="solid"
      borderRadius="small"
      borderWidth="xxxsmall"
      borderColor={borderColor}
    >
      <Inline space="large" flexWrap="nowrap" alignItems="center">
        {iconName && (
          <Icon
            tone={tone}
            size="xlarge"
            iconName={iconName}
            accessibilityLabel={iconName}
          />
        )}

        {children && <AlertBody body={children} tone={tone} />}
      </Inline>

      <Box marginLeft="xlarge">
        <Inline space="large" flexWrap="nowrap" alignItems="center">
          <AlertButton buttonProps={buttonProps} tone={tone} />
          <CloseIcon onCloseClick={onCloseClick} tone={tone} />
        </Inline>
      </Box>
    </Box>
  );
}

function AlertBody({
  body,
  tone
}: {
  body: string | React.ReactNode;
  tone: AlertTone;
}) {
  if (typeof body === "string") return <Text tone={tone}>{body}</Text>;
  return <Box>{body}</Box>;
}

function CloseIcon({
  onCloseClick,
  tone
}: Pick<Props, "onCloseClick" | "tone">) {
  return onCloseClick ? (
    <IconButton
      tone={tone}
      blend
      size="small"
      onClick={onCloseClick}
      accessibilityLabel="close"
      iconName="cross"
    />
  ) : null;
}

function AlertButton({
  buttonProps,
  tone
}: Pick<Props, "buttonProps" | "tone">) {
  if (!buttonProps) return null;

  type DefaultProps = { size: ButtonSize; fill: ButtonFill };
  const defaultProps: DefaultProps = { size: "small", fill: "outlined" };

  const { text } = buttonProps;

  if (isButton(buttonProps)) {
    return (
      <Button tone={tone} {...buttonProps} {...defaultProps}>
        {text}
      </Button>
    );
  }

  if (isButtonLink(buttonProps)) {
    return (
      <ButtonLink tone={tone} {...buttonProps} {...defaultProps}>
        {text}
      </ButtonLink>
    );
  }

  return null;
}

function getColours(tone: AlertTone) {
  const theme = useTheme();

  const backgroundColorRole: Role = `${tone}-low-surface`;
  const backgroundColor = theme[backgroundColorRole];

  const borderColorRole: Role = `${tone}-low-content`;
  const borderColor = theme[borderColorRole];

  return { backgroundColor, borderColor };
}
